import { BaseParser } from '../BaseParser';

/**
 * SoFi Bank Parser
 * Handles SoFi CSV export format
 */
export class SoFiParser extends BaseParser {
  constructor() {
    super();
    this.name = 'SoFiParser';
    this.priority = 10;
  }

  /**
   * Detect if this is a SoFi CSV file
   */
  detect(header, bank) {
    let confidence = 0;

    // Bank hint
    if (bank === 'sofi') {
      confidence += 50;
    }

    // SoFi format: Date, Description, Type, Amount, Current balance, Status
    if (header.includes('date,description,type,amount,current balance,status')) {
      confidence += 50;
    }

    return confidence;
  }

  /**
   * Parse SoFi CSV
   * Format: Date, Description, Type, Amount, Current balance, Status
   * Date format: YYYY-MM-DD
   * Type: DIRECT_PAY, DEPOSIT, WITHDRAWAL, OTHER, DIRECT_DEPOSIT
   */
  parse(lines, filename) {
    const transactions = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      try {
        const row = this.parseCSVLine(line);
        if (row.length < 6) continue;

        const [date, description, type, amount, currentBalance, status] = row;
        const amountValue = this.parseAmount(amount);

        // SoFi: negative = expense/payment, positive = deposit/income
        // Amount sign is correct as-is
        const transactionType = this.inferSoFiType(type, amountValue);

        transactions.push(this.createTransaction({
          id: this.generateId(date, description, amount),
          date: this.parseDateISO(date),
          postDate: this.parseDateISO(date),
          description: description || 'Unknown',
          amount: amountValue,
          type: transactionType,
          category: 'Uncategorized', // Will be handled by categorization pipeline
          memo: `Type: ${type}, Status: ${status}`,
          balance: this.parseAmount(currentBalance),
          source: filename || 'SoFi',
          rawData: row
        }));
      } catch (error) {
        this.log(`Error parsing line ${i}: ${error.message}`, 'error');
      }
    }

    return transactions;
  }

  /**
   * Infer transaction type for SoFi transactions
   */
  inferSoFiType(sofiType, amount) {
    switch (sofiType.toUpperCase()) {
      case 'DIRECT_PAY':
        return amount < 0 ? 'Payment' : 'Refund';
      case 'DEPOSIT':
      case 'DIRECT_DEPOSIT':
        return 'Deposit';
      case 'WITHDRAWAL':
        return 'Withdrawal';
      case 'OTHER':
        return amount < 0 ? 'Expense' : 'Income';
      default:
        return amount < 0 ? 'Expense' : 'Income';
    }
  }

  getInfo() {
    return {
      name: this.name,
      priority: this.priority,
      supportedBanks: ['sofi'],
      formats: ['SoFi Bank']
    };
  }
}
